import { useCallback, useRef } from 'react'
import AsyncSelect from 'react-select/async'
import { searchLocations } from '@astro/vedastro'
import { ksSelectStyles } from './StudioBits'

/** A VedAstro location → a react-select option carrying the full coords. */
const toOption = (loc) =>
  loc ? { value: `${loc.Name}|${loc.Latitude},${loc.Longitude}`, label: loc.Name, location: loc } : null

/**
 * Coordinates typed directly → an exact-coordinates option. Different atlases
 * pin the same town up to a few km apart, and on a cusp-sensitive chart (an
 * Ascendant within arcseconds of a varga boundary) that alone flips a
 * divisional chart's house ring — so a reading can only be reproduced exactly
 * by entering the reference atlas's own coordinates, which a name search
 * cannot offer. Accepted forms:
 *   decimal   "29.2833, 77.7167"            (lat, lon)
 *   deg-min   "29 N 17, 77 E 43"            (AstroSage's own display format)
 *             "29°17'N 77°43'E" / "29N17 77E43" — any order, comma optional
 */
function coordOption(q) {
  const dec = /^(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)$/.exec(q)
  if (dec) {
    const lat = Number(dec[1])
    const lon = Number(dec[2])
    if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null
    const name = `${Math.abs(lat)}°${lat < 0 ? 'S' : 'N'}, ${Math.abs(lon)}°${lon < 0 ? 'W' : 'E'}`
    return toOption({ Name: name, Latitude: lat, Longitude: lon })
  }

  // Degree-minute tokens: "29°17'N" (minutes before hemisphere) or "29 N 17"
  // (after, as AstroSage prints). Exactly one N/S + one E/W token, and the
  // input must contain nothing else, so city names can never false-match.
  const TOKEN = /(\d{1,3})(?:[°\s.:]+(\d{1,2}(?:\.\d+)?)['′]?)?\s*([NSEW])(?:[\s.:]*(\d{1,2}(?:\.\d+)?))?/gi
  const parts = []
  for (const m of q.matchAll(TOKEN)) {
    if (m[2] && m[4]) return null // minutes on both sides of the hemisphere
    const deg = Number(m[1])
    const min = Number(m[2] ?? m[4] ?? 0)
    const hemi = m[3].toUpperCase()
    if (min >= 60) return null
    parts.push({
      axis: hemi === 'N' || hemi === 'S' ? 'lat' : 'lon',
      value: (deg + min / 60) * (hemi === 'S' || hemi === 'W' ? -1 : 1),
      label: `${deg}°${min ? `${min}′` : ''}${hemi}`,
    })
  }
  if (parts.length !== 2 || q.replace(TOKEN, '').replace(/[\s,;]+/g, '') !== '') return null
  const lat = parts.find((p) => p.axis === 'lat')
  const lon = parts.find((p) => p.axis === 'lon')
  if (!lat || !lon || Math.abs(lat.value) > 90 || Math.abs(lon.value) > 180) return null
  return toOption({
    Name: `${lat.label}, ${lon.label}`,
    Latitude: Number(lat.value.toFixed(6)),
    Longitude: Number(lon.value.toFixed(6)),
  })
}

const portalStyles = {
  ...ksSelectStyles,
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
}

/**
 * Birth-place autocomplete — type a city and pick from live VedAstro
 * suggestions, each resolved to exact latitude/longitude so no second
 * geocode is needed. Debounced (350ms) and gated to 2+ characters so
 * keystrokes don't hammer the API; results cached per query.
 *
 * Controlled: `value` is a `{ Name, Longitude, Latitude }` object (or null),
 * `onChange` receives the same (or null when cleared).
 */
export default function LocationSelect({ value, onChange, inputId, placeholder = 'Search your birth city…' }) {
  const timer = useRef(null)

  const loadOptions = useCallback(
    (input) =>
      new Promise((resolve) => {
        clearTimeout(timer.current)
        const q = (input || '').trim()
        const exact = coordOption(q)
        if (exact) return resolve([exact])
        if (q.length < 2) return resolve([])
        timer.current = setTimeout(async () => {
          try {
            const list = await searchLocations(q)
            resolve(list.map(toOption))
          } catch {
            resolve([])
          }
        }, 350)
      }),
    [],
  )

  return (
    <AsyncSelect
      inputId={inputId}
      cacheOptions
      defaultOptions={false}
      loadOptions={loadOptions}
      value={toOption(value)}
      onChange={(opt) => onChange(opt?.location || null)}
      isClearable
      placeholder={placeholder}
      styles={portalStyles}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      loadingMessage={() => 'Searching…'}
      noOptionsMessage={({ inputValue }) =>
        inputValue && inputValue.trim().length >= 2 ? 'No matching places found' : 'Type at least 2 letters'
      }
    />
  )
}
